import React, { useEffect, useRef, useState } from "react";
import { connect } from "react-redux";
import { useNavigate } from "react-router-dom";
import { Formik, Field, Form } from "formik";
import { Button, FormGroup, Label } from "reactstrap";
import SimpleReactValidator from "simple-react-validator";
import {
    LockSvg,
    EyeOutlineSvg,
    EyeOffOutlineSvg,
    VendorShieldSvg,
    VendorTickSvg,
} from "../../../components/CommonSvg";
import {
    clearVendorChangePassword,
    vendorChangePasswordRequest,
} from "../../../redux/vendor/auth/changePassword/action";
import { ApiResponseMessage } from "../../../helper/tosterHelper";
import { DANGER } from "../../../constant/commonConstants";

const INITIAL_VALUES = {
    currentPassword: "",
    newPassword: "",
    confirmPassword: "",
};

const RULES = [
    { label: "At least 6 characters", test: (v) => v.length >= 6 },
    { label: "One lowercase letter", test: (v) => /[a-z]/.test(v) },
    { label: "One uppercase letter", test: (v) => /[A-Z]/.test(v) },
    { label: "One number", test: (v) => /\d/.test(v) },
    { label: "One special character (!@#$%^&*_=)", test: (v) => /[!@#$%^&*_=]/.test(v) },
];

const STRENGTH = ["Too weak", "Weak", "Fair", "Good", "Strong"];

const VendorChangePasswordSplit = (props) => {
    const { changePassword, clearChangePassword, changePasswordData } = props;
    const navigate = useNavigate();
    const validator = useRef(new SimpleReactValidator());
    const [shown, setShown] = useState({});
    const [newPassword, setNewPassword] = useState("");

    const passed = RULES.filter((rule) => rule.test(newPassword)).length;
    const strength = newPassword ? Math.max(1, passed) : 0;

    const toggleShown = (name) =>
        setShown((prev) => ({ ...prev, [name]: !prev[name] }));

    const handleSubmit = (values) => {
        if (!validator.current.allValid()) {
            validator.current.showMessages();
            return;
        }
        if (RULES.some((rule) => !rule.test(values.newPassword))) {
            ApiResponseMessage("New password does not meet all the rules", DANGER);
            return;
        }
        if (values.newPassword !== values.confirmPassword) {
            ApiResponseMessage("New password & confirm password must be same", DANGER);
            return;
        }
        if (values.newPassword === values.currentPassword) {
            ApiResponseMessage(
                "New password must be different from the current one",
                DANGER
            );
            return;
        }

        changePassword(values);
    };

    useEffect(() => {
        if (!changePasswordData) return;
        if (changePasswordData.status === true) {
            navigate("/vendor/dashboard");
        }
        clearChangePassword();
    }, [changePasswordData]);

    const field = (name, label, placeholder, autoComplete, onType) => (
        <FormGroup className="va-field">
            <Label className="va-label" htmlFor={name}>
                {label} <span className="va-required">*</span>
            </Label>
            <div className="va-control">
                <span className="va-ic">
                    <LockSvg />
                </span>
                <Field
                    id={name}
                    name={name}
                    type={shown[name] ? "text" : "password"}
                    className="va-input"
                    placeholder={placeholder}
                    autoComplete={autoComplete}
                    maxLength={99}
                    onKeyUp={onType}
                />
                <button
                    type="button"
                    className="va-eye"
                    aria-label={shown[name] ? "Hide password" : "Show password"}
                    onClick={() => toggleShown(name)}
                >
                    {shown[name] ? <EyeOffOutlineSvg /> : <EyeOutlineSvg />}
                </button>
            </div>
        </FormGroup>
    );

    return (
        <div className="vp-page vp-cs-page">
            <div className="vp-cs-card">
                <aside className="vp-cs-side">
                    <div className="vp-cs-in">
                        <span className="vp-cs-badge">
                            <VendorShieldSvg />
                        </span>
                        <h2>Keep your account secure</h2>
                        <p>
                            Your new password replaces the old one everywhere you
                            sign in to the vendor portal.
                        </p>

                        <ul className="vp-cs-rules">
                            {RULES.map((rule) => {
                                const done = rule.test(newPassword);
                                return (
                                    <li key={rule.label} className={done ? "ok" : ""}>
                                        <VendorTickSvg done={done} />
                                        {rule.label}
                                    </li>
                                );
                            })}
                        </ul>
                    </div>
                </aside>

                <div className="vp-cs-main">
                    <div className="vp-cs-in">
                    <h1>Change Password</h1>
                    <p className="vp-cs-sub">
                        Enter your current password, then choose a new one.
                    </p>

                    <Formik initialValues={INITIAL_VALUES} onSubmit={handleSubmit}>
                        {({ values }) => (
                            <Form role="form" className="vp-cs-form">
                                {field(
                                    "currentPassword",
                                    "Current Password",
                                    "Enter your current password",
                                    "current-password"
                                )}
                                {validator.current.message(
                                    "current password",
                                    values.currentPassword,
                                    "required",
                                    {
                                        messages: {
                                            required: "Current password is required",
                                        },
                                    }
                                )}

                                {field(
                                    "newPassword",
                                    "New Password",
                                    "Enter your new password",
                                    "new-password",
                                    (e) => setNewPassword(e.target.value)
                                )}
                                {validator.current.message(
                                    "new password",
                                    values.newPassword,
                                    "required|min:6",
                                    {
                                        messages: {
                                            required: "New password is required",
                                            min: "Password must be at least 6 characters",
                                        },
                                    }
                                )}

                                <div className={`vp-cs-meter s-${strength}`}>
                                    <span className="bars">
                                        {[1, 2, 3, 4, 5].map((step) => (
                                            <i key={step} className={step <= strength ? "on" : ""} />
                                        ))}
                                    </span>
                                    <em>{newPassword ? STRENGTH[strength - 1] : " "}</em>
                                </div>

                                {field(
                                    "confirmPassword",
                                    "Confirm New Password",
                                    "Confirm your new password",
                                    "new-password"
                                )}
                                {validator.current.message(
                                    "confirm password",
                                    values.confirmPassword,
                                    "required",
                                    {
                                        messages: {
                                            required: "Confirm password is required",
                                        },
                                    }
                                )}

                                {values.confirmPassword.length > 0 &&
                                    values.newPassword !== values.confirmPassword && (
                                        <p className="vp-cs-err">
                                            Both new passwords must match.
                                        </p>
                                    )}

                                <div className="vp-cs-actions">
                                    <Button
                                        type="button"
                                        color="secondary"
                                        outline
                                        onClick={() => navigate(-1)}
                                    >
                                        Cancel
                                    </Button>
                                    <Button type="submit" color="primary">
                                        Update Password
                                    </Button>
                                </div>
                            </Form>
                        )}
                    </Formik>
                    </div>
                </div>
            </div>
        </div>
    );
};

const mapStateToProps = ({ VendorChangePassword }) => {
    return { changePasswordData: VendorChangePassword?.changePassword };
};

const mapDispatchToProps = (dispatch) => {
    return {
        changePassword: (data) => dispatch(vendorChangePasswordRequest(data)),
        clearChangePassword: () => dispatch(clearVendorChangePassword()),
    };
};

export default connect(
    mapStateToProps,
    mapDispatchToProps
)(VendorChangePasswordSplit);
